import { Component, inject, OnInit, Signal } from '@angular/core';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { SharedModule } from '../../../shared/shared.module';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  BookingsScope,
  BookingsState,
} from '../../../core/store/bookings/bookings.state';
import { endOfDay, format, startOfDay } from 'date-fns';
import { ApiError } from '../../../core/models/common/api-error';
import { Store } from '@ngrx/store';
import { selectBookingsState } from '../../../core/store/reducers';
import { BookingsService } from '../../../core/services/bookings.service';
import { ToastService } from '../../../core/services/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-export-excel-modal',
  imports: [SharedModule],
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="submit()">
      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <app-date-input [control]="form.controls.from" [label]="'bookings.export.date-from' | transloco" />
        <app-date-input [control]="form.controls.until" [label]="'bookings.export.date-until' | transloco" />
      </div>

      <div class="space-y-2">
        @for (property of propertiesForm.controls; track property.value.name) {
          <div class="flex items-center gap-4">
            <app-single-checkbox-input
              [name]="property.value.name + '_' + 'active'"
              [control]="property.controls.active"
              [marginBottom]="false"
            />

            <app-text-input
              class="grow"
              [control]="property.controls.label"
              [name]="property.value.name + '_' + 'label'"
              [marginBottom]="false"
              [marginTop]="false"
            />
          </div>
        }
      </div>
    </app-modal>
  `,
})
export class ExportExcelModalComponent
  extends ModalBaseComponent
  implements OnInit
{
  store = inject(Store);
  bookingsService = inject(BookingsService);
  toastService = inject(ToastService);
  modalService = inject(ModalService);
  private translocoService = inject(TranslocoService);
  bookingsState: Signal<BookingsState> =
    this.store.selectSignal(selectBookingsState);
  form = new FormGroup({
    from: new FormControl(null),
    until: new FormControl(null),
  });

  bookingProperties = [
    { name: 'reference', labelKey: 'bookings.export.col-reference', active: true },
    { name: 'start', labelKey: 'bookings.export.col-start', active: true },
    { name: 'end', labelKey: 'bookings.export.col-end', active: true },
    { name: 'created_at', labelKey: 'bookings.export.col-created-at', active: true },
    { name: 'room.id', labelKey: 'bookings.export.col-room-id', active: true },
    { name: 'room.name', labelKey: 'bookings.export.col-room-name', active: true },
    { name: 'status', labelKey: 'bookings.export.col-status', active: true },
    { name: 'updated_at', labelKey: 'bookings.export.col-updated-at', active: true },
    { name: 'name', labelKey: 'bookings.export.col-name', active: true },
    { name: 'full_name', labelKey: 'bookings.export.col-full-name', active: true },
    { name: 'company', labelKey: 'bookings.export.col-company', active: true },
    { name: 'full_address', labelKey: 'bookings.export.col-full-address', active: true },
    { name: 'email', labelKey: 'bookings.export.col-email', active: true },
    { name: 'phone', labelKey: 'bookings.export.col-phone', active: true },
    { name: 'eventType.name', labelKey: 'bookings.export.col-event-type', active: true },
    { name: 'comment', labelKey: 'bookings.export.col-comment', active: true },
    { name: 'rate.name', labelKey: 'bookings.export.col-rate-name', active: true },
    { name: 'attendance', labelKey: 'bookings.export.col-attendance', active: true },
    { name: 'gross_total', labelKey: 'bookings.export.col-gross-total', active: true },
    {
      name: 'gross_total_share',
      labelKey: 'bookings.export.col-gross-total-share',
      active: true,
    },
    {
      name: 'first_invoice_coupon_code',
      labelKey: 'bookings.export.col-coupon-code',
      active: true,
    },
    {
      name: 'first_invoice_coupon_discount',
      labelKey: 'bookings.export.col-coupon-discount',
      active: true,
    },
    { name: 'invoices_total', labelKey: 'bookings.export.col-invoices-total', active: true },
    { name: 'balance', labelKey: 'bookings.export.col-balance', active: true },
    { name: 'invoicing_status', labelKey: 'bookings.export.col-invoicing-status', active: true },
  ];

  propertiesForm = new FormArray<
    FormGroup<{
      name: FormControl<string>;
      label: FormControl<string>;
      active: FormControl<boolean>;
    }>
  >([]);

  ngOnInit() {
    this.modalInstance.config.update((config) => ({
      ...config,
      title: this.translocoService.translate('bookings.export.modal-title'),
      okAction: {
        ...config.okAction,
        title: this.translocoService.translate('bookings.export.button-export'),
      },
    }));

    this.form.patchValue({
      from: this.bookingsState()[BookingsScope.All].from
        ? [new Date(this.bookingsState()[BookingsScope.All].from)]
        : null,
      until: this.bookingsState()[BookingsScope.All].to
        ? [new Date(this.bookingsState()[BookingsScope.All].to)]
        : null,
    });

    const ebp = JSON.parse(localStorage.getItem('exportBookingsProperties'));
    for (const property of this.bookingProperties) {
      const savedProperty = (ebp ?? []).find((el) => el.name === property.name);
      this.propertiesForm.push(
        new FormGroup({
          name: new FormControl(property.name),
          label: new FormControl(
            savedProperty?.label?.length
              ? savedProperty?.label
              : this.translocoService.translate(property.labelKey),
          ),
          active: new FormControl(savedProperty?.active ?? property.active),
        }),
      );
    }
  }

  async submit() {
    try {
      localStorage.setItem(
        'exportBookingsProperties',
        JSON.stringify(this.propertiesForm.value),
      );
    } catch (exception) {}

    try {
      this.modalInstance.saving = true;
      const state = this.bookingsState()[BookingsScope.All];
      const data = await this.bookingsService.exportAsExcel({
        from: this.form.value.from?.length
          ? DatetimeHelper.dateToApiFormat(
              startOfDay(this.form.value.from[0]),
              true,
            )
          : null,
        to: this.form.value.until?.length
          ? DatetimeHelper.dateToApiFormat(
              endOfDay(this.form.value.until[0]),
              true,
            )
          : null,
        order_by: state.order?.map((orderBy) => [
          orderBy.field,
          orderBy.order,
        ]) as any,
        room_ids: state.roomsFilter.ids ?? [],
        status: state.bookingsFilter.status ?? [],
        search: state.search || undefined,
        attributes: this.propertiesForm.value
          .filter((el) => el.active)
          .map((el) => ({ name: el.name, label: el.label })),
      });
      const blob = new Blob([data], { type: 'application/ms-excel' });
      const fileName = `Export-Location-Salles-Reservations-${format(
        new Date(),
        'yyyy-MM-dd',
      )}.xlsx`;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      this.modalService.close();
    } catch (exception) {
      console.error(exception);
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.modalInstance.saving = false;
    }
  }
}
